import { useSearchParams } from "react-router-dom";
import { ClipboardList, MessageSquare, Mail, ArrowRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { HealthyContactEmbed } from "@/components/site/HealthyContactEmbed";
import { Button } from "@/components/ui/button";
import type { BookingMode } from "@/data/certificateBookingActions";
import { getConfirmationFormUrl } from "@/data/confirmationForms";
import { findCertificateBySlug } from "@/data/certificateSlugs";
import { useLang } from "@/i18n/LangContext";
import { PageMeta } from "@/components/site/PageMeta";

const parseBookingMode = (value: string | null): BookingMode | null =>
  value === "digital" || value === "onsite" ? value : null;

const Bekraftelse = () => {
  const { t, lang, prefix } = useLang();
  const c = t.confirmation;
  const [searchParams] = useSearchParams();
  const intygParam = searchParams.get("intyg");
  const slug = intygParam ? findCertificateBySlug(intygParam, lang) ?? intygParam : null;
  const mode = parseBookingMode(searchParams.get("besok"));
  const formUrl = getConfirmationFormUrl(slug, mode);


  const title = lang === "sv" ? "Bekräftelse | Intygsläkaren" : "Confirmation | Intygsläkaren";
  const desc =
    lang === "sv"
      ? "Tack för din bokning hos Intygsläkaren. Du får snart en bekräftelse via SMS och e-post."
      : "Thank you for booking with Intygsläkaren. You will receive an SMS and email confirmation shortly.";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        <section className="relative overflow-hidden bg-gradient-soft pt-10 pb-12 sm:pt-14 lg:pt-20 lg:pb-16">
          <div className="absolute inset-x-0 top-0 -z-10 h-[480px] bg-[radial-gradient(ellipse_at_top,_hsl(var(--primary-soft))_0%,_transparent_60%)]" />
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="font-display text-4xl font-extrabold tracking-tight text-primary sm:text-5xl lg:text-6xl">
                {c.title}
              </h1>
              <p className="mt-5 text-base text-black sm:text-lg">{c.subtitle}</p>
            </div>

            {formUrl ? (
              <div className="mx-auto mt-8 max-w-3xl rounded-3xl border border-border bg-card p-6 text-left shadow-card sm:p-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                    <ClipboardList className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-display text-2xl font-semibold text-foreground">{c.formTitle}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {c.formText}
                    </p>
                  </div>
                </div>
                <HealthyContactEmbed
                  src={formUrl}
                  title={c.formTitle}
                  className="mt-6 overflow-hidden rounded-2xl border border-border bg-background"
                  iframeClassName="h-[75vh] min-h-[560px] w-full border-0"
                />
                <p className="mt-3 text-center text-sm text-muted-foreground">
                  <a href={formUrl} target="_blank" rel="noopener" className="underline-offset-2 hover:underline">
                    {c.formCta}
                  </a>
                </p>
              </div>
            ) : null}

            <div className="mx-auto mt-8 max-w-3xl">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-border bg-card p-6 text-left shadow-card">
                  <div className="flex items-start gap-4">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                      <MessageSquare className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-display text-xl font-semibold text-foreground">{c.smsTitle}</h2>
                      <p className="mt-2 text-sm text-muted-foreground">{c.smsText}</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-6 text-left shadow-card">
                  <div className="flex items-start gap-4">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-display text-xl font-semibold text-foreground">{c.emailTitle}</h2>
                      <p className="mt-2 text-sm text-muted-foreground">{c.emailText}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-3xl border border-primary/20 bg-primary p-8 text-center text-primary-foreground shadow-elevated sm:p-10">
                <h2 className="font-display text-2xl font-semibold sm:text-3xl">{c.seeYou}</h2>
                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Button asChild variant="onPrimary" size="xl">
                    <a href={`${prefix}/`}>{c.home}</a>
                  </Button>
                  <Button asChild variant="ghostOnPrimary" size="xl" className="border-white text-white hover:bg-white hover:text-black">
                    <a href={`${prefix}/lakarintyg`}>
                      {c.allCerts}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Bekraftelse;
